import React from 'react';
import classNames from 'classnames';

import './button.scss';

type ValidButtonType = 'submit' | 'reset' | 'button';

type ButtonProps = {
  styleModifier?: string;
  className?: string;
  additionalAttributes?: Record<string, any>;
  href?: string;
  target?: string;
  type?: string;
  svgIcon?: string;
  screenReaderText?: string;
  buttonText?: string;
  buttonTextMobile?: string;
  onClick?: () => void;
  tag?: string;
};

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      styleModifier,
      className,
      additionalAttributes = {},
      href,
      target,
      type,
      svgIcon,
      screenReaderText,
      buttonText,
      buttonTextMobile,
      onClick = () => {},
      tag,
    },
    ref,
  ) => {
    const Tag = (tag ?? (href ? 'a' : 'button')) as any;

    let screenReaderTextToRender = 'Button';
    if (screenReaderText) {
      screenReaderTextToRender = screenReaderText;
    } else if (buttonText) {
      screenReaderTextToRender = buttonText;
    } else if (buttonTextMobile) {
      screenReaderTextToRender = buttonTextMobile;
    }

    const validType = Tag === 'a' ? type : validButtonType(type);

    return (
      <Tag
        className={classNames('button', styleModifier, className)}
        href={href}
        target={target}
        {...additionalAttributes}
        onClick={onClick}
        ref={ref}
        type={validType}>
        <span className="h-offscreen">{screenReaderTextToRender}</span>
        <span
          className={classNames('button-wrapper', {
            'button-wrapper--svg-icon': svgIcon,
          })}
          aria-hidden="true"
          role="presentation">
          {svgIcon && (
            <span className="button-icon">
              <span className={classNames('svg-icon', svgIcon)} />
            </span>
          )}
          {buttonTextMobile && (
            <span className="button-text button-text--mobile">
              {buttonTextMobile}
            </span>
          )}
          {buttonText && <span className="button-text">{buttonText}</span>}
        </span>
      </Tag>
    );
  },
);

// W3C Validator
const validButtonType = (type?: string): ValidButtonType => {
  const VALID_BUTTON_TYPES: ValidButtonType[] = ['submit', 'reset', 'button'];

  if (type && VALID_BUTTON_TYPES.includes(type as ValidButtonType)) {
    return type as ValidButtonType;
  }
  return 'button';
};

Button.displayName = 'Button';

export default Button;
